import React from 'react';
import { FOCUS_RING } from './focusRing';

type PillButtonSize = 'xsmall' | 'small' | 'medium';
type PillButtonVariant = 'default' | 'inverted';
/**
 * `tab` squares the top corners so the pill reads as attached to whatever sits above it —
 * a row of controls hanging under an input, rather than free-floating pills.
 */
type PillButtonShape = 'pill' | 'tab';
/**
 * `assistant` is the chrome for AI-authored chips (e.g. `ChangeReview.Jump`): a fixed
 * violet-on-white look with its own border, independent of `variant`/`selected`.
 */
type PillButtonTone = 'default' | 'assistant';

interface PillButtonProps {
    label: string;
    icon?: React.ReactNode;
    className?: string;
    selected?: boolean;
    disabled?: boolean;
    interactive?: boolean;
    size?: PillButtonSize;
    variant?: PillButtonVariant;
    shape?: PillButtonShape;
    tone?: PillButtonTone;
    /** Sits after the label — a caret on a pill that opens a menu. */
    trailingIcon?: React.ReactNode;
    onClick?: () => void;
}

const BASE_CLASSES =
    'inline-flex items-center justify-center gap-1.5 font-medium whitespace-nowrap shrink-0 transition-colors duration-150 ease-in-out';

const SHAPE_CLASSES: Record<PillButtonShape, string> = {
    pill: 'rounded-[var(--malachite-radius-10,10px)]',
    tab: 'rounded-b-[var(--malachite-radius-10,10px)] rounded-t-none',
};

// Box-sizing is border-box, so trading `border-0` for a 1px border only eats into the padding
// box — it never grows the element past the `h-[...]` each size already fixes. Only one of these
// ever applies per render, so there's no `border-0`/`border` class collision to referee.
const TONE_BORDER_CLASSES: Record<PillButtonTone, string> = {
    default: 'border-0',
    assistant: 'border border-solid border-[var(--malachite-tag-assistant-border)]',
};

const ASSISTANT_COLORS =
    'bg-[var(--malachite-card-background)] text-[var(--malachite-assistant-foreground)]';

const SIZE_CLASSES: Record<
    PillButtonSize,
    { text: string; padding: string; paddingWithIcon: string }
> = {
    // For a row that labels other content rather than being read on its own — a context
    // strip above a chat, say. The only size that drops below the 13px the others share.
    xsmall: {
        text: 'text-[length:var(--malachite-font-size-xsmall,11px)] h-[22px] leading-none',
        padding: 'px-2',
        paddingWithIcon: 'pl-1 pr-1.5',
    },
    small: {
        text: 'text-[length:var(--malachite-font-size-small,13px)] h-[var(--malachite-control-height-small,28px)] leading-none',
        padding: 'px-2.5',
        paddingWithIcon: 'pl-1.5 pr-2',
    },
    medium: {
        text: 'text-[length:var(--malachite-font-size-small,13px)] h-[var(--malachite-control-height-medium,32px)] leading-none',
        padding: 'px-2.5',
        paddingWithIcon: 'pl-1.5 pr-2',
    },
};

const STATIC_COLORS =
    'bg-[var(--malachite-pillbutton-static-background)] text-[var(--malachite-pillbutton-static-foreground)]';

const CLICKABLE_TAIL = 'active:opacity-60 cursor-pointer';

const VARIANT_COLORS: Record<PillButtonVariant, { base: string; selected: string; hover: string }> =
    {
        default: {
            base: 'bg-[var(--malachite-pillbutton-background)] text-[var(--malachite-pillbutton-foreground)]',
            selected:
                'bg-[var(--malachite-pillbutton-hover-background)] text-[var(--malachite-pillbutton-hover-foreground)]',
            hover: 'hover:bg-[var(--malachite-pillbutton-hover-background)] hover:text-[var(--malachite-pillbutton-hover-foreground)] active:bg-[var(--malachite-pillbutton-pressed-background)] active:text-[var(--malachite-pillbutton-pressed-foreground)]',
        },
        inverted: {
            base: 'bg-[var(--malachite-pillbutton-inverted-background)] text-[var(--malachite-pillbutton-inverted-foreground)]',
            selected:
                'bg-[var(--malachite-pillbutton-inverted-hover-background)] text-[var(--malachite-pillbutton-inverted-hover-foreground)]',
            hover: 'hover:bg-[var(--malachite-pillbutton-inverted-hover-background)] hover:text-[var(--malachite-pillbutton-inverted-hover-foreground)] active:bg-[var(--malachite-pillbutton-inverted-pressed-background)] active:text-[var(--malachite-pillbutton-inverted-pressed-foreground)]',
        },
    };

export const PillButton: React.FC<PillButtonProps> = ({
    label = 'Label',
    icon,
    className = '',
    selected = false,
    disabled = false,
    interactive = true,
    size = 'small',
    variant = 'default',
    shape = 'pill',
    tone = 'default',
    trailingIcon,
    onClick,
}) => {
    const sizeBlock = SIZE_CLASSES[size];
    const padding = icon ? sizeBlock.paddingWithIcon : sizeBlock.padding;
    const variantBlock = VARIANT_COLORS[variant];
    const isInteractiveLook = interactive && !disabled;
    const isClickable = isInteractiveLook && !!onClick;
    const clickableTail = isClickable ? ` ${CLICKABLE_TAIL}` : '';

    let colorClasses: string;
    if (tone === 'assistant') {
        // Assistant chips read the same in every interaction state — the click affordance is
        // the shared `active:opacity-60` from `clickableTail`, not a colour swap.
        colorClasses = isClickable ? `${ASSISTANT_COLORS}${clickableTail}` : ASSISTANT_COLORS;
    } else if (!interactive) {
        colorClasses = STATIC_COLORS;
    } else if (selected) {
        colorClasses = isInteractiveLook
            ? `${variantBlock.selected}${clickableTail}`
            : variantBlock.selected;
    } else if (isInteractiveLook) {
        colorClasses = `${variantBlock.base} ${variantBlock.hover}${clickableTail}`;
    } else {
        colorClasses = variantBlock.base;
    }

    const disabledClasses = disabled ? 'opacity-50 cursor-not-allowed pointer-events-none' : '';

    const merged = `${BASE_CLASSES} ${SHAPE_CLASSES[shape]} ${sizeBlock.text} ${padding} ${TONE_BORDER_CLASSES[tone]} ${colorClasses} ${disabledClasses} ${className}`;

    const iconSlot = icon ? (
        <span className="inline-flex items-center justify-center w-4 h-4 shrink-0 [&>img]:max-w-full [&>img]:max-h-full [&>img]:object-contain">
            {icon}
        </span>
    ) : null;

    const trailingSlot = trailingIcon ? (
        <span className="inline-flex shrink-0 items-center justify-center">{trailingIcon}</span>
    ) : null;

    if (isClickable) {
        return (
            <button type="button" className={`${FOCUS_RING} ${merged}`} onClick={onClick}>
                {iconSlot}
                {label}
                {trailingSlot}
            </button>
        );
    }

    return (
        <div className={merged}>
            {iconSlot}
            {label}
            {trailingSlot}
        </div>
    );
};

export default PillButton;
